import React, { useEffect, useMemo, useRef } from "react"
import { ChevronLeft } from "lucide-react"
import Form from "react-bootstrap/Form"
import AdminSaveStatusIndicator from "./AdminSaveStatusIndicator.jsx"

/**
 * @param {{
 *   locations: Array<{ id?: number | string, name?: string, lodge_code?: string, use_print_dialog?: boolean }>,
 *   currentLocationId?: number | null,
 *   rowStatus: Record<string, string>,
 *   onBack: () => void,
 *   onToggle: (locationId: number, value: boolean) => void,
 *   disabled?: boolean,
 *   setRowStatus: React.Dispatch<React.SetStateAction<Record<string, string>>>,
 * }} props
 */
export default function AdminPrintDialogLocationsMenu({
    locations,
    currentLocationId,
    rowStatus,
    onBack,
    onToggle,
    disabled = false,
    setRowStatus,
}) {
    const listRef = useRef(null)
    const currentRowRef = useRef(null)
    const didInitialScrollRef = useRef(false)

    const options = useMemo(
        () =>
            [...(locations ?? [])]
                .filter((l) => l?.lodge_code)
                .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })),
        [locations]
    )

    useEffect(() => {
        if (didInitialScrollRef.current) {
            return
        }

        function scrollToCurrent() {
            const list = listRef.current
            const current = currentRowRef.current
            if (!list || !current) {
                return false
            }

            const listRect = list.getBoundingClientRect()
            const rowRect = current.getBoundingClientRect()

            if (rowRect.top < listRect.top) {
                list.scrollTop -= listRect.top - rowRect.top
            } else if (rowRect.bottom > listRect.bottom) {
                list.scrollTop += rowRect.bottom - listRect.bottom
            }

            didInitialScrollRef.current = true
            return true
        }

        let innerRafId = 0
        const outerRafId = window.requestAnimationFrame(() => {
            scrollToCurrent()
            innerRafId = window.requestAnimationFrame(scrollToCurrent)
        })

        return () => {
            window.cancelAnimationFrame(outerRafId)
            window.cancelAnimationFrame(innerRafId)
        }
    }, [options, currentLocationId])

    function handleToggle(locationId, checked) {
        if (disabled || rowStatus[locationId] === "saving") {
            return
        }

        setRowStatus((prev) => ({ ...prev, [locationId]: "saving" }))
        onToggle(locationId, checked)
    }

    return (
        <div className="lodge-admin-print-dialog-submenu">
            <div className="lodge-admin-print-dialog-submenu-header">
                <button
                    type="button"
                    className="lodge-admin-print-dialog-back"
                    onClick={(e) => {
                        e.stopPropagation()
                        onBack()
                    }}
                    aria-label="Back to quick settings"
                >
                    <ChevronLeft size={16} aria-hidden="true" />
                </button>
                <span className="lodge-admin-print-dialog-submenu-title">Use Print Dialog</span>
            </div>
            <ul
                ref={listRef}
                className="lodge-admin-print-dialog-list list-unstyled mb-0"
                role="list"
            >
                {options.map((location) => {
                    const id = Number(location.id)
                    const isCurrent = id === Number(currentLocationId)
                    const status = rowStatus[id] ?? "idle"
                    const isSaving = status === "saving"

                    return (
                        <li
                            key={id}
                            ref={isCurrent ? currentRowRef : undefined}
                            className={
                                "lodge-admin-print-dialog-row" +
                                (isCurrent ? " lodge-admin-print-dialog-row--current" : "")
                            }
                        >
                            <Form.Check
                                type="switch"
                                id={`lodge-admin-print-dialog-${id}`}
                                className="lodge-admin-print-dialog-switch"
                                label={
                                    <span className="lodge-admin-print-dialog-row-label">
                                        {location.name ?? location.lodge_code}
                                    </span>
                                }
                                checked={Boolean(location.use_print_dialog)}
                                disabled={disabled || isSaving}
                                onChange={(e) => handleToggle(id, e.target.checked)}
                            />
                            <AdminSaveStatusIndicator status={status} />
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
